import { RootState } from "@/app/store";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { auth as authClient } from "@/core/api";
import { showNotification } from "@/components/basic/NotificationBar/notificationBarSlice";
import { trackLogin } from "../helpers/tracking";

export interface State {
  isAuthenticate: boolean;
  status: "idle" | "loading" | "failed";
  userInfo?: {
    [key: string]: any;
  };
}

const initialState: State = {
  isAuthenticate: false,
  status: "idle",
  userInfo: undefined,
};

export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { dispatch }
  ) => {
    const response = await authClient.login({ email, password });
    let message = "";
    if (typeof response === "object") {
      message = "Login successful.";
      // Link this browser's search session to the authenticated user (dual-run)
      try {
        const nrSid = localStorage.getItem("nr_session_id");
        if (nrSid && email) {
          fetch("/api/capture/session.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_id:       nrSid,
              email:            email.trim().toLowerCase(),
              is_authenticated: true,
            }),
            keepalive: true,
          }).catch(() => {});
        }
      } catch (_) {}
    } else {
      message = "Email or password is incorrect.";
    }

    if (message) {
      dispatch(showNotification(message));
    }
    return response;
  }
);

export const getUserInfo = createAsyncThunk("auth/getUserInfo", async () => {
  const response = await authClient.getUserInfo();
  return response;
});

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }: { email: string }, { dispatch }) => {
    const response = await authClient.forgotPassword({ email });
    let message = "";

    // Generic response regardless of whether the email exists — prevents account enumeration
    message = "If that email is registered, we've sent a link to reset your password.";

    if (message) {
      dispatch(showNotification(message));
    }
    return response;
  }
);

export const addressSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload;
        if (typeof payload === "object") {
          const transformData = {};
          Object.keys(payload).forEach((key) => {
            if (key !== "password") {
              transformData[key.replace("session_", "")] = payload[key];
            }
          });
          state.userInfo = transformData;
          state.isAuthenticate = true;
          trackLogin('email');
        } else {
          state.isAuthenticate = false;
        }
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload;
        if (typeof payload === "object") {
          const transformData = {};
          Object.keys(payload).forEach((key) => {
            if (key !== "password") {
              transformData[key.replace("session_", "")] = payload[key];
            }
          });
          state.userInfo = transformData;
          state.isAuthenticate = true;
        } else {
          state.isAuthenticate = false;
        }
      });
  },
});

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectUserInfo = (state: RootState) => state.auth.userInfo;

export const selectIsAuthenticate = (state: RootState) =>
  state.auth.isAuthenticate;

export default addressSlice.reducer;
